import React, { useState, useEffect, useCallback } from "react";
import { useQuery } from "@apollo/client";
import {
  GetLmsUsersDocument,
  GetLmsUsersQuery,
  GetUsersAppDocument,
  GetUsersAppQuery,
  GetUsersInput,
  UserRoleEnum,
} from "../../generated/graphql";
import { useResetPageOnSearch } from "../../hooks";
import { CircularProgress, Container, Table } from "@material-ui/core";
import {
  StyledTableContainer,
  StyledTableRow,
  StyledTableCell,
  StyledTableHeader,
  StyledSearchInput,
  StyledPaginationContainer,
  StyledPaginationButton,
  StyledPaginationInfo,
  LoadingContainer,
  ErrorContainer,
  EmptyContainer,
} from "./LmsUsersList.styles";

interface SearchFilter {
  searchKey?: string;
}

export const LmsUsersList: React.FC = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [inputValue, setInputValue] = useState("");
  const [activeFilter, setActiveFilter] = useState<SearchFilter>({});

  const searchKey =
    activeFilter.searchKey && activeFilter.searchKey.length >= 4
      ? activeFilter.searchKey
      : undefined;

  useResetPageOnSearch({ searchKey, setPage });

  const getUsersData = {
    limit: rowsPerPage,
    skip: page * rowsPerPage,
    searchKey,
  };

  const { data, loading, error } = useQuery<GetLmsUsersQuery>(
    GetLmsUsersDocument,
    {
      variables: { ...getUsersData },
    }
  );

  const debouncedSetFilter = useCallback(
    debounce((value: string) => {
      setActiveFilter({
        searchKey: value || undefined,
      });
    }, 500),
    []
  );

  useEffect(() => {
    debouncedSetFilter(inputValue);
  }, [inputValue, debouncedSetFilter]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  function debounce<T extends (...args: any[]) => void>(
    func: T,
    delay: number
  ): (...args: Parameters<T>) => void {
    let timeoutId: NodeJS.Timeout;
    return (...args: Parameters<T>) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  }

  const handlePreviousPage = () => {
    if (page > 0) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    const totalUsers = data?.getLMSUsers?.users?.length || 0;
    if (totalUsers === rowsPerPage) {
      setPage(page + 1);
    }
  };

  const renderTableContent = () => {
    if (loading) {
      return (
        <tbody>
          <StyledTableRow>
            <StyledTableCell colSpan={3}>
              <LoadingContainer>
                <CircularProgress color="inherit" />
              </LoadingContainer>
            </StyledTableCell>
          </StyledTableRow>
        </tbody>
      );
    }

    if (error) {
      return (
        <tbody>
          <StyledTableRow>
            <StyledTableCell colSpan={3}>
              <ErrorContainer>
                При загрузке пользователей произошла ошибка
              </ErrorContainer>
            </StyledTableCell>
          </StyledTableRow>
        </tbody>
      );
    }

    if (!data?.getLMSUsers?.users?.length) {
      return (
        <tbody>
          <StyledTableRow>
            <StyledTableCell colSpan={3}>
              <EmptyContainer>Список пользователей пуст</EmptyContainer>
            </StyledTableCell>
          </StyledTableRow>
        </tbody>
      );
    }

    const users = data.getLMSUsers.users;

    return (
      <>
        <thead>
          <StyledTableRow>
            <StyledTableHeader>Имя</StyledTableHeader>
            <StyledTableHeader>Фамилия</StyledTableHeader>
            <StyledTableHeader>Почта</StyledTableHeader>
            <StyledTableHeader>Зарегистрирован из</StyledTableHeader>
            <StyledTableHeader>Роль</StyledTableHeader>
          </StyledTableRow>
        </thead>
        <tbody>
          {users.map((user) => (
            <StyledTableRow key={user.id}>
              <StyledTableCell>{user.firstName}</StyledTableCell>
              <StyledTableCell>{user.lastName}</StyledTableCell>
              <StyledTableCell>{user.email}</StyledTableCell>
              <StyledTableCell>{user.registeredFrom}</StyledTableCell>
              <StyledTableCell>
                {user.role?.role === UserRoleEnum.Teacher
                  ? "Учитель"
                  : "Другое"}
              </StyledTableCell>
            </StyledTableRow>
          ))}
        </tbody>
      </>
    );
  };

  const renderPagination = () => {
    const totalUsers = data?.getLMSUsers?.users?.length || 0;
    const startIndex = page * rowsPerPage + 1;
    const endIndex = page * rowsPerPage + totalUsers;

    return (
      <StyledPaginationContainer>
        <StyledPaginationInfo>
          Показано {startIndex}-{endIndex} записей
        </StyledPaginationInfo>
        <div>
          <StyledPaginationButton
            onClick={handlePreviousPage}
            disabled={page === 0}
          >
            Назад
          </StyledPaginationButton>
          <StyledPaginationButton
            onClick={handleNextPage}
            disabled={totalUsers < rowsPerPage}
          >
            Далее
          </StyledPaginationButton>
        </div>
      </StyledPaginationContainer>
    );
  };

  return (
    <Container maxWidth="lg">
      <h1>Список пользователей связанных с LMS</h1>

      <StyledSearchInput
        type="text"
        placeholder="Поиск по имени или фамилии (минимум 4 символа)"
        value={inputValue}
        onChange={handleSearchChange}
      />

      {data?.getLMSUsers?.users && renderPagination()}

      <StyledTableContainer>
        <Table>{renderTableContent()}</Table>
      </StyledTableContainer>

      {data?.getLMSUsers?.users && renderPagination()}
    </Container>
  );
};
